import {
    CleverReachList,
    NewsletterFormData,
    resendNewsletterConfirmation,
    subscribeToNewsletter
} from '@cineamo/newsletter';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import Button from '@/components/button/Button';
import FormCheckbox from '@/components/FormCheckbox';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconSuccessful16 } from '@cineamo/ui/src/icons/ic-successful';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { track } from '@/utils/server-side-tracking';

import FormInput from './FormInput';

export default function NewsletterForm() {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showResendOption, setShowResendOption] = useState(false);
    const [lastSubmittedEmail, setLastSubmittedEmail] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors }
    } = useForm<NewsletterFormData>({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            privacyConsent: false
        }
    });

    // Register privacyConsent with validation
    React.useEffect(() => {
        register('privacyConsent', { required: t('newsletter.validation.privacy-required') });
    }, [register, t]);

    const privacyConsent = watch('privacyConsent');

    const onSubmit = async (data: NewsletterFormData) => {
        if (!data.privacyConsent) {
            showToast({
                toastType: toast.error,
                messageTitle: t('newsletter.error.title'),
                messageBody: t('newsletter.validation.privacy-required')
            });
            return;
        }

        setIsSubmitting(true);

        try {
            // Get current URL for tracking
            const currentUrl = window.location.href;

            // Get user IP (optional - fallback to server default if not available)
            // Note: For privacy reasons, this might not always work in modern browsers
            let userIP: string | undefined;
            try {
                const ipResponse = await fetch('https://api.ipify.org?format=json');
                if (ipResponse.ok) {
                    const ipData = await ipResponse.json();
                    userIP = ipData.ip;
                }
            } catch {
                // IP will fallback to '127.0.0.1' in the server action
            }

            const result = await subscribeToNewsletter(
                domainConfiguration.cinema,
                data,
                CleverReachList.general,
                currentUrl,
                userIP
            );

            if (result.success) {
                showToast({
                    toastType: toast.success,
                    messageIcon: <IconSuccessful16 />,
                    messageTitle: t('newsletter.success.title'),
                    messageBody: t('newsletter.success.confirmation-message'),
                    backgroundColor: domainConfiguration.style.primaryColor,
                    textColor: domainConfiguration.style.onPrimaryColor
                });

                // Track successful newsletter subscription
                track('Newsletter Subscription', {
                    cinema: domainConfiguration.cinema?.name,
                    domain: domainConfiguration.domain,
                    cinemaId: domainConfiguration.cinema?.id,
                    hasFirstName: !!data.firstName,
                    hasLastName: !!data.lastName
                });

                // Show confirmation screen
                setIsSuccess(true);
            } else if (result.isDuplicate) {
                // Handle duplicate email - show resend option
                setLastSubmittedEmail(data.email);
                setShowResendOption(true);
                showToast({
                    toastType: toast.error,
                    messageTitle: t('newsletter.error.duplicate.title'),
                    messageBody: t('newsletter.error.duplicate.message')
                });
            } else {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('newsletter.error.title'),
                    messageBody: t('newsletter.error.message')
                });
            }
        } catch {
            showToast({
                toastType: toast.error,
                messageTitle: t('newsletter.error.title'),
                messageBody: t('newsletter.error.network-message')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendConfirmation = async () => {
        if (!lastSubmittedEmail) return;

        setIsSubmitting(true);

        try {
            // Get current URL and IP again
            const currentUrl = window.location.href;
            let userIP: string | undefined;
            try {
                const ipResponse = await fetch('https://api.ipify.org?format=json');
                if (ipResponse.ok) {
                    const ipData = await ipResponse.json();
                    userIP = ipData.ip;
                }
            } catch {
                // Silent fallback
            }

            const success = await resendNewsletterConfirmation(
                domainConfiguration.cinema,
                lastSubmittedEmail,
                CleverReachList.general,
                currentUrl,
                userIP
            );

            if (success) {
                setShowResendOption(false);
                showToast({
                    toastType: toast.success,
                    messageIcon: <IconSuccessful16 />,
                    messageTitle: t('newsletter.resend.success.title'),
                    messageBody: t('newsletter.resend.success.message'),
                    backgroundColor: domainConfiguration.style.primaryColor,
                    textColor: domainConfiguration.style.onPrimaryColor
                });
            } else {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('newsletter.resend.error.title'),
                    messageBody: t('newsletter.resend.error.message')
                });
            }
        } catch {
            showToast({
                toastType: toast.error,
                messageTitle: t('newsletter.resend.error.title'),
                messageBody: t('newsletter.resend.error.message')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="backdrop-blur-sm rounded-12 p-12 shadow-lg text-center bg-[var(--surface-color)]">
                <IconSuccessful16 className="size-[48px] mx-auto mb-4 text-[var(--primary-color)]" />
                <h3 className="text-headline-24 mb-2 text-[var(--on-background-color)]">
                    {t('newsletter.confirmation.title')}
                </h3>
                <p className="text-body-16 text-[var(--on-background-color)]">{t('newsletter.confirmation.message')}</p>
            </div>
        );
    }

    return (
        <div className="backdrop-blur-sm rounded-12 p-12 shadow-lg bg-[var(--surface-color)]">
            <form
                className="space-y-24"
                onSubmit={handleSubmit(onSubmit)}>
                {/* Contact Information */}
                <div className="grid md:grid-cols-2 gap-6">
                    <FormInput
                        name="firstName"
                        label={t('newsletter.form.first-name')}
                        type="text"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                    />
                    <FormInput
                        name="lastName"
                        label={t('newsletter.form.last-name')}
                        type="text"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                    />
                </div>

                <FormInput
                    name="email"
                    label={t('newsletter.form.email')}
                    type="email"
                    register={register}
                    registerOptions={{ required: true }}
                    required
                />

                {/* Privacy Consent Checkbox */}
                <FormCheckbox
                    name="privacy-consent"
                    label={t('newsletter.form.privacy-consent')}
                    checked={privacyConsent}
                    onChange={(checked) => setValue('privacyConsent', checked)}
                    required
                    error={errors.privacyConsent?.message}
                />

                {/* Submit Button */}
                <div className="pt-4">
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 text-body-16 font-semibold transition-all duration-200 rounded-8"
                        css={{
                            backgroundColor: domainConfiguration.style.primaryColor,
                            color: domainConfiguration.style.onPrimaryColor,
                            opacity: isSubmitting ? 0.6 : 1
                        }}
                        leadingIcon={isSubmitting ? <IconLoading16 className="animate-spin" /> : undefined}>
                        {isSubmitting ? t('newsletter.form.submitting') : t('newsletter.form.submit')}
                    </Button>
                </div>

                {/* Resend Confirmation Option */}
                {showResendOption && (
                    <div className="pt-4 text-center">
                        <p className="text-body-14 mb-2 text-[var(--on-background-color)]">
                            {t('newsletter.resend.description')}
                        </p>
                        <button
                            type="button"
                            onClick={handleResendConfirmation}
                            disabled={isSubmitting}
                            className={cn(
                                'text-body-14 font-semibold underline hover:no-underline transition-all duration-200',
                                isSubmitting ? 'opacity-60' : 'opacity-100'
                            )}>
                            {t('newsletter.resend.button')}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
}
